/* eslint-disable @typescript-eslint/default-param-last */
import { at } from 'lodash';

import { IFileType } from '../file-type';

import { IValidationService, FileInputValue } from './validation.interface';

const NUMBER_B_IN_MB = 1048576;
const DEFAULT_SIZE_LIMIT = 20;

export const validateFile = async (
  file: File,
  message: string,
  fileTypes: string[],
  fileTypeChecker: IFileType,
) => {
  const fileType = file?.name.includes('.sig')
    ? 'application/pgp-signature'
    : await fileTypeChecker.fileTypeFromBlob(file);

  return !fileTypes.includes(fileType) ||
    file.size > DEFAULT_SIZE_LIMIT * NUMBER_B_IN_MB
    ? message
    : undefined;
};

export const getImageResolution = (
  file: File,
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = reject;
      img.src = reader.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export class ValidationService implements IValidationService {
  constructor(private readonly fileType: IFileType) {}

  /** Создать функцию, которая в случае "пустого" значения выведет заданное сообщение об ошибке
   * @param {string} [message=Обязательное поле] Сообщение об ошибке в случае, если значение "пустое"
   * @returns {(value: unknown) => string | undefined}
   * */
  required =
    <T, K extends keyof T>(message = 'Обязательное поле') =>
    (value?: T[K]) =>
      typeof value === 'undefined' ||
      value === null ||
      (typeof value === 'boolean' && value === false) ||
      (typeof value === 'string' && value.trim() === '') ||
      (Array.isArray(value) && value.length === 0)
        ? message
        : undefined;

  /**
   * Создать функцию, которая будет возвращать предупреждающее сообщение,
   * если заданное значение не относится к допустимым типам файлов или файл превышает допустимый размер
   * @param {string} message Сообщение, указывающее, что значение имеет недопустимый тип или размер
   * @param {{ field: string; fileTypes: string[] }} options Опции, содержат список допустимых типов
   * @returns {(value: unknown) => Promise<string | undefined>}
   * */
  requiredIfFile = <T, K extends keyof T>(
    message = 'Обязательное поле',
    options: { field: string; fileTypes: string[] },
  ) => {
    const fileTypeChecker = this.fileType;

    return async function validateValue(
      value?: T[K],
    ): Promise<string | undefined> {
      if (
        !value ||
        typeof value === 'string' ||
        typeof (value as unknown as FileInputValue)?.source === 'string'
      )
        return undefined;

      if (Array.isArray(value)) {
        for (const arrItem of value) {
          const error = await validateValue(arrItem);
          if (error) return error;
        }
        return undefined;
      } else {
        const result = await validateFile(
          ((value as unknown as FileInputValue)?.source as File) ||
            (value as unknown as File),
          message,
          options.fileTypes,
          fileTypeChecker,
        );

        return result;
      }
    };
  };

  maxValue =
    <T, K extends keyof T>(max: number, message: string) =>
    (value?: T[K]) =>
      Number(value) > Number(max) ? message : undefined;

  minValue =
    <T, K extends keyof T>(min: number, message: string) =>
    (value?: T[K]) =>
      Number(value) <= Number(min) ? message : undefined;

  /** Создать функуию, которая будет возвращать предупреждающее сообщение, если переданное значение превышает указанный в опциях лимит
   * @param {string} message Сообщение, указывающее, что значение превышает лимит
   * @param {{ field: string; sizeLimit: number }} options Опции, содержат лимит
   * */
  sizeLimit = <T, K extends keyof T>(
    message = 'Объем файла слишком большой',
    options: { field: string; sizeLimit: number },
  ) =>
    function validateValue(
      value?: T[K] | FileInputValue,
      allValues?: Partial<T>,
    ): string | undefined {
      const sizeLimit = options.sizeLimit * NUMBER_B_IN_MB;

      if (
        !value ||
        typeof value === 'string' ||
        typeof (value as FileInputValue)?.source === 'string'
      )
        return undefined;

      if (Array.isArray(value)) {
        const res = value.reduce((error: string | undefined, arrItem) => {
          return error || validateValue(arrItem, allValues);
        }, undefined);
        return res;
      } else {
        return at(allValues, options.field)[0] &&
          ((value as FileInputValue).source as File)?.size > sizeLimit
          ? message
          : undefined;
      }
    };

  /**
   * Проверяет, что ширина и высота каждого файла не меньше заданных значений.
   * При несоответствии возвращает сообщение об ошибке для первого невалидного файла.
   * Функция-валидатор, возвращающая строку ошибки или undefined.
   * @param {string} message - Сообщение, добавляемое к ошибке, если разрешение недостаточно.
   * @param {{ minWidth: number; minHeight: number }} options - Минимально допустимые размеры.
   */
  imageResolutionCheck = (
    _: string,
    options: { minWidth: number; minHeight: number },
  ) => {
    return async function (value?: FileInputValue[] | FileInputValue) {
      if (value === undefined) return undefined;

      const files = Array.isArray(value) ? value : [value];

      for (const { source: file } of files) {
        if (typeof file === 'string') return undefined;

        try {
          const { width, height } = await getImageResolution(file);
          if (width < options.minWidth)
            return 'Недопустимые параметры изображения';

          if (height < options.minHeight)
            return 'Недопустимые параметры изображения';
        } catch (error) {
          return `${file.name}: Ошибка при загрузке изображения`;
        }
      }
      return undefined;
    };
  };

  validUrl =
    (message = 'Значение поля должно быть ссылкой') =>
    (value: string | undefined) => {
      if (!value) return undefined;

      try {
        new URL(value);
        return undefined;
      } catch (_) {
        return message;
      }
    };

  /** Создать функцию, которая будет возвращать предупреждающее сообщение,
   * если в тексте отсутствует поддерживаемый протокол
   * @param {string} [message=Ссылка должна содержать поддерживаемый протокол] Сообщение об ошибке
   * @returns {(value: string | undefined) => string | undefined}
   */
  containsSupportedProtocol =
    (message = 'Ссылка должна содержать поддерживаемый протокол') =>
    (value: string | undefined) => {
      if (!value) return undefined;

      const SUPPORTED_PROTOCOLS = [
        'http:',
        'https:',
        'mailto:',
        'sms:',
        'tel:',
      ];

      try {
        const parsedUrl = new URL(value);
        const hasSupportedProtocol = SUPPORTED_PROTOCOLS.includes(
          parsedUrl.protocol,
        );

        if (!hasSupportedProtocol) {
          return message;
        }

        if (parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:') {
          const hasDoubleSlash = value.includes('://');
          return hasDoubleSlash
            ? undefined
            : 'Ссылка должна содержать // после протокола';
        }

        return undefined;
      } catch {
        return message;
      }
    };
}
